// Home.js – Startseite (/): Hero, Notdienst, Kennzahlen, Textabschnitte mit Karussells (Text1, Text21), Säulen und Leistungskarten.
import "./Home.css";
import "./custom.css";
import "bootstrap/dist/css/bootstrap.min.css";
import HeroSection from "./HeroSection";
import Notdienst from "./Notdienst";
import Text1 from "./Text1";
import Text21 from "./Text21";
import { Link } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import BeforeAfter from "./BeforeAfter";
import MulcherVorher from "./images/VorherNacher/MulcherVorher.webp";
import MulcherNachher from "./images/VorherNacher/MulcherNachher.webp";
import usePageMeta from "../../usePageMeta";

const stats = [
  { value: "10+",  label: "Jahre Erfahrung" },
  { value: "150+", label: "Abgeschlossene Projekte" },
  // Als Leitziel formuliert, nicht als messbare Umweltaussage (UWG, pauschale Umweltaussagen seit 27.09.2026)
  { value: "Naturnah", label: "Unser Leitziel bei jedem Einsatz" },
  { value: "SH",   label: "Schleswig-Holstein" },
];

const pillars = [
  {
    title: "Fachgerechte Planung",
    text: "Jeder Einsatz beginnt mit einer sorgfältigen Standortanalyse. Wir planen präzise, ökologisch und rechtssicher nach BNatSchG.",
  },
  {
    title: "Bodenschonende Technik",
    text: "Präzision statt Kraft: Mit Seiltechnik, Funkraupe und Forstseilwinde schützen wir Boden, Wurzeln und Bestand konsequent bei jedem Einsatz.",
  },
  {
    title: "Nachhaltige Partnerschaft",
    text: "Wir denken in Jahrzehnten. Was wir heute tun, prägt den Wald und die Landschaft von morgen, und das ist uns ernst.",
  },
];

const services = [
  {
    num: "01",
    title: "Baumpflege",
    desc: "Kronenpflege, Baumkontrolle und Habitatbaumerhalt nach CODIT-Prinzip mit Kletter- und Seiltechnik sowie Arbeitsbühne.",
    link: "/baumpflege",
    cta: "Zur Baumpflege",
  },
  {
    num: "02",
    title: "Landschaftspflege",
    desc: "Bodenschonendes Mulchen mit der funkgesteuerten Raupe für Böschungen, Magerrasen und Naturschutzflächen in jedem Gelände.",
    link: "/mulchen",
    cta: "Zur Landschaftspflege",
  },
  {
    num: "03",
    title: "Baumfällung",
    desc: "Gefahrbaum- und Spezialfällung mit Seilklettertechnik und Arbeitsbühne, auch neben Gebäuden, an Straßen und in engen Gärten.",
    link: "/baumfaellung",
    cta: "Zur Baumfällung",
  },
];

function Home() {
  usePageMeta({
    path: "/",
    title: "Forst & Flur: Baumpflege & Forstarbeiten in Schleswig-Holstein",
    description:
      "Baumpflege, Baumfällung, Mulchen und bodenschonende Rückearbeiten aus Probsteierhagen, in Kiel, Lübeck und ganz Schleswig-Holstein. Jetzt Beratung anfragen.",
  });

  return (
    <div>
      <HeroSection />
      <Notdienst />

      {/* Stats Bar */}
      <div className="ff-stats-bar">
        <Container>
          <Row className="text-center gy-3">
            {stats.map((s, i) => (
              <Col key={i} xs={6} md={3}>
                <div className="ff-stat__value">{s.value}</div>
                <div className="ff-stat__label">{s.label}</div>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      <Text1 />
      <Text21 />

      {/* Process Strip – 3 Säulen */}
      <div className="ff-process-strip">
        <Container>
          <Row className="gy-0">
            {pillars.map((p, i) => (
              <Col key={i} xs={12} md={4}>
                <div className="ff-process-card">
                  <h3 className="ff-process-card__title">{p.title}</h3>
                  <p className="ff-process-card__text">{p.text}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* Service Cards */}
      <div className="ff-services-section" id="leistungen">
        <Container className="py-5">
          <div className="text-center mb-5">
            <span className="ff-eyebrow">Was wir bieten</span>
            <h2 className="ff-section-title">Unsere Leistungen</h2>
          </div>
          <Row className="gy-4">
            {services.map((s, i) => (
              <Col key={i} xs={12} md={6} lg={4}>
                <Link
                  to={s.link}
                  className="ff-service-card"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  <div className="ff-service-card__num">{s.num}</div>
                  <h3 className="ff-service-card__title">{s.title}</h3>
                  <p className="ff-service-card__desc">{s.desc}</p>
                  <span className="ff-service-card__cta">{s.cta}</span>
                </Link>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* Ergebnis-Teaser: Vorher/Nachher beim Mulchen */}
      <div className="ff-result-section">
        <Container className="py-5">
          <Row className="align-items-center justify-content-center gy-4">
            <Col xs={12} md={5} lg={4}>
              <BeforeAfter
                before={MulcherVorher}
                width={675}
                height={1197}
                after={MulcherNachher}
                ratio={[4, 5]}
                objectPosition="center 70%"
                beforeAlt="Vorher: dichter Aufwuchs aus Gehölzen, Brombeeren und hohem Gras am Waldrand"
                afterAlt="Nachher: gemulchte, freigestellte Fläche vor den erhaltenen Altbäumen, der Geräteträger steht links im Hintergrund"
                caption="Regler ziehen oder auf „Vorher“ und „Nachher“ tippen"
              />
            </Col>
            <Col xs={12} md={7} lg={6}>
              <span className="ff-eyebrow">Ergebnis</span>
              <h2 className="ff-section-title">Landschaftspflege im Vergleich</h2>
              <p className="ff-result-section__text">
                Dieselbe Stelle am Gehölzrand: links zugewachsen, rechts nach dem Mulchen mit
                unserem Geräteträger. Bodenschonend und mit Rücksicht auf den Bestand: Ziehen Sie den Regler
                und sehen Sie den Unterschied.
              </p>
              <Link to="/mulchen" className="btn-ff-primary" onClick={() => window.scrollTo(0, 0)}>
                Mehr zur Landschaftspflege
              </Link>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default Home;
